/**
 * OneSignal Web SDK loader + opt-in helper.
 *
 * - Loads `OneSignalSDK.page.js` once on first call.
 * - Initialises with the public APP_ID from REACT_APP_ONESIGNAL_APP_ID.
 * - Provides `linkUser(userId)` to attach the logged-in user's DB id as
 *   `external_id` so the backend can target this user by `user.id`.
 * - Provides `requestPermission()` to trigger the browser's native permission
 *   prompt (must be triggered from a user click, not on page load).
 */
const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

let sdkLoaded = false;
let initialised = false;

async function loadSdk() {
    if (sdkLoaded || typeof window === "undefined") return;
    sdkLoaded = true;
    await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
        s.async = true;
        s.defer = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

async function ensureInit() {
    if (!APP_ID) return false;
    await loadSdk();
    if (initialised) return true;
    initialised = true;
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    await new Promise((resolve) => {
        window.OneSignalDeferred.push(async (OneSignal) => {
            try {
                await OneSignal.init({
                    appId: APP_ID,
                    serviceWorkerPath: "/OneSignalSDKWorker.js",
                    notifyButton: { enable: false },
                    allowLocalhostAsSecureOrigin: true,
                });
            } catch (e) {
                console.warn("OneSignal init failed:", e);
            }
            resolve();
        });
    });
    return true;
}

export async function linkUser(userId) {
    if (!userId) return;
    const ok = await ensureInit();
    if (!ok) return;
    window.OneSignalDeferred.push(async (OneSignal) => {
        try {
            await OneSignal.login(String(userId));
        } catch (e) {
            console.warn("OneSignal.login failed:", e);
        }
    });
}

export async function logoutUser() {
    if (!initialised || typeof window === "undefined" || !window.OneSignalDeferred) return;
    window.OneSignalDeferred.push(async (OneSignal) => {
        try { await OneSignal.logout(); } catch { /* ignore */ }
    });
}

export async function requestPermission() {
    const ok = await ensureInit();
    if (!ok) {
        return { granted: false, reason: "not_configured" };
    }
    return new Promise((resolve) => {
        window.OneSignalDeferred.push(async (OneSignal) => {
            try {
                const granted = await OneSignal.Notifications.requestPermission();
                resolve({ granted: Boolean(granted) });
            } catch (e) {
                resolve({ granted: false, reason: String(e) });
            }
        });
    });
}

export async function getSubscriptionState() {
    const ok = await ensureInit();
    if (!ok) return { configured: false };
    return new Promise((resolve) => {
        window.OneSignalDeferred.push(async (OneSignal) => {
            try {
                const optedIn = OneSignal.User.PushSubscription.optedIn;
                const id = OneSignal.User.PushSubscription.id || "";
                resolve({ configured: true, optedIn: Boolean(optedIn), subscriptionId: id });
            } catch (e) {
                resolve({ configured: true, optedIn: false, error: String(e) });
            }
        });
    });
}

export const isConfigured = Boolean(APP_ID);
